import React, { useState } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  FormFeedback,
  FormGroup,
  Row,
  Col,
} from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import FormLabel from "components/Extra/FormLabel"
import { FormInput } from "components/Extra/FormInput"
import { useTranslation } from "react-i18next"

interface FilterByDateRangeModalProps {
  onDateRangeChange?: (startDate: string, endDate: string) => void
  className?: string
  style?: React.CSSProperties
}

export const FilterByDateRangeModal: React.FC<FilterByDateRangeModalProps> = ({ onDateRangeChange, className, style }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [displayDates, setDisplayDates] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 1)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  })
  const { t } = useTranslation()

  const validation = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    initialValues: {
      startDate: "",
      endDate: "",
    },
    validationSchema: Yup.object({
      startDate: Yup.date()
        .required(t("common_startDateRequired") as string)
        .max(
          Yup.ref("endDate"),
          t("common_invalidDateRange") as string
        ),
      endDate: Yup.date()
        .required(t("common_endDateRequired") as string)
        .min(
          Yup.ref("startDate"),
          t("common_invalidDateRange") as string
        ),
    }),
    onSubmit: values => {
      if (onDateRangeChange) {
        onDateRangeChange(values.startDate, values.endDate)
      }
      setDisplayDates({
        startDate: values.startDate,
        endDate: values.endDate
      })
      toggle()
    },
  })

  const toggle = () => {
    setIsOpen(!isOpen)
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' }
    return date.toLocaleDateString('en-GB', options).replace(',', '')
  }

  const startDateDisplay = formatDate(displayDates.startDate)
  const endDateDisplay = formatDate(displayDates.endDate)

  return (
    <div style={{ flex: 1, ...style}}  className={`d-flex justify-content-end ${className}`}>
      <span
        style={{
          cursor: "pointer",
        }}
        onClick={toggle}
      >
        {startDateDisplay} to {endDateDisplay}{" "}
        <i className="fas fa-angle-down"></i>
      </span>

      <Modal isOpen={isOpen} toggle={toggle} size="lg">
        <ModalHeader toggle={toggle} tag="h3">
          <h4 className="text-capitalize fw-semibold font-size-24">
            {t("common_dateRange")}
          </h4>
        </ModalHeader>
        <ModalBody>
          <Form
            onSubmit={e => {
              e.preventDefault()
              validation.handleSubmit()
              return false
            }}
          >
            <Row>
              <Col md="6">
                <FormInput
                  label={t("common_startDate") as string}
                  name="startDate"
                  type="date"
                  validation={validation}
                  required
                />
              </Col>
              <Col md="6">
                <FormInput
                  label={t("common_endDate") as string}
                  name="endDate"
                  type="date"
                  validation={validation}
                  required
                />
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <button
                type="submit"
                className={`btn bg-gradiant-blue text-white border-0 save-customer`}
                disabled={!validation.isValid}
              >
                {t("common_submit")}
              </button>
              <button
                onClick={toggle}
                type="button"
                className="btn btn-cancel text-primary border border-primary"
              >
                {t("common_cancel")}
              </button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </div>
  )
}

